import { workspaces as workspacesApi } from "apis/callables";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import { LIBRARY_RECENT_MAX, PROMPTS_RECENT_MAX } from "common/constants";
import validateEduDomains from "common/utils/validateEduDomains";
import Cookies from "js-cookie";
import { getWorkspacesMetadata } from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import StorageModel from "js/core/storage/storageModel";
import isConnected from "js/core/utilities/isConnected";
import { auth } from "js/firebase/auth";
import { ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.USER);

const User = StorageModel.extend({
    root: "users",

    initialize() {
        if (this.id) {
            // cookie for analytics
            Cookies.set("userId", this.id);
        }
    },

    async generateLoadPromise(...args) {
        // Awaiting the base generate promise first
        await StorageModel.prototype.generateLoadPromise.apply(this, ...args);

        const user = auth().currentUser;
        if (this.get("email") !== user.email) {
            // we found an issue where there are some users (who signed up
            // through google sign in) in our user table that do not have an
            // email even though the auth table does have an email, so this
            // is to backfill the user table with emails from the auth table.
            this.update({ email: user.email });
        }

        // fetch trial detail from API
        if (isConnected.connected) {
            try {
                await Promise.all([
                    this.loadWorkspacesMetadata(),
                    this.loadAvailableTrialDays()
                ]);
            } catch (err) {
                logger.error(err, "[user] load() workspacesApi.getWorkspacesMetadata() failed", { id: this.id });
            }
        }
    },

    async loadWorkspacesMetadata(workspaceIds = null) {
        const metadata = await getWorkspacesMetadata({ ids: workspaceIds });
        if (workspaceIds) {
            this.workspacesMetadata = _.cloneWith([...this.workspacesMetadata.filter(metadata => !workspaceIds.includes(metadata.id)), ...metadata]);
        } else {
            this.workspacesMetadata = metadata;
        }
    },

    getDefaultWorkspaceMetadata() {
        return this.workspacesMetadata.filter(metadata => metadata.userRole === WorkspaceUserRole.OWNER).sort((a, b) => a.createdAt - b.createdAt)[0];
    },

    getDefaultBasicWorkspaceMetadata() {
        return this.workspacesMetadata.filter(metadata => metadata.userRole === WorkspaceUserRole.OWNER && metadata.plan.tier === WorkspacePlanTier.BASIC).sort((a, b) => a.createdAt - b.createdAt)[0];
    },

    async loadAvailableTrialDays() {
        const availableTrialDays = await workspacesApi.getAvailableTrialDays({});
        this.availableTrialDays = availableTrialDays;
    },

    loadMinimum() {
        return StorageModel.prototype.load.apply(this, arguments);
    },

    getEmail() {
        return this.getAuthUser().email;
    },

    getAuthUser() {
        return auth().currentUser;
    },

    getAvatarUrl() {
        let { photoURL } = this.getAuthUser() || {};
        return this.get("avatarUrl") || photoURL;
    },

    getDisplayName() {
        return this.getAuthUser().displayName || this.getAuthUser().email;
    },

    checkIfUserAndPresentationBelongToSameOrg(presentationOrgId) {
        return this.has("orgs") && this.get("orgs")[presentationOrgId];
    },

    isEligibleForEduPlan(email) {
        return !validateEduDomains(email || this.get("email"));
    },

    async updateEmailAddress(email) {
        // Use custom email verification flow instead of Firebase's verifyBeforeUpdateEmail
        await Api.verifyEmail.post({ newEmail: email });

        const hasEduPlan = this.workspacesMetadata.some(metadata => metadata.plan.tier === WorkspacePlanTier.EDU);
        if (hasEduPlan && !this.isEligibleForEduPlan(email)) {
            ShowMessageDialog({
                title: "You are currently on the Edu plan. Changing your email will downgrade you to the Basic plan.",
                message: "If you are still a student, you can reapply for the education plan after validating your new email address."
            });
            await Api.eduPlan.delete();
        }
    },

    waitForVerifiedEmail() {
        let user = this.getAuthUser();
        let checkVerifiedInterval;
        return new Promise(resolve => {
            clearInterval(checkVerifiedInterval);
            checkVerifiedInterval = setInterval(async () => {
                if (user) {
                    await user.reload();
                    if (user.emailVerified) {
                        await auth().currentUser.getIdToken(true);
                        clearInterval(checkVerifiedInterval);
                        resolve();
                    }
                } else {
                    clearInterval(checkVerifiedInterval);
                }
            }, 5000);
        });
    },

    getLibrarySettings() {
        return _.defaults(_.cloneDeep(this.get("librarySettings")), {
            viewType: "grid",
            sortField: "modifiedAt",
            sortReverse: true,
            thumbnailSize: 300,
            recentPresentations: [],
            scaleInterp: 3,
        });
    },

    getRecentPresentations() {
        return Object.values(this.get("librarySettings")?.recentPresentations ?? []).filter(Boolean);
    },

    addPresentationsToRecent(presentationIds) {
        const recentPresentations = Object.values(this.get("librarySettings")?.recentPresentations ?? []);
        const updatedPresentations = [...recentPresentations.filter(p => !presentationIds.includes(p)), ...presentationIds];
        while (updatedPresentations.length > LIBRARY_RECENT_MAX) {
            updatedPresentations.shift();
        }
        this.update({
            librarySettings: { recentPresentations: updatedPresentations }
        });
        return this.updatePromise;
    },

    getRecentPrompts(type) {
        const recentPrompts = this.get("recentPrompts") ?? {};
        return recentPrompts[type] ?? [];
    },

    addPromptToRecent(prompt, type) {
        prompt = prompt.trim();
        const recentPrompts = _.cloneDeep(this.get("recentPrompts") ?? {});
        if (!recentPrompts[type]) {
            recentPrompts[type] = [];
        }
        recentPrompts[type] = [prompt, ...recentPrompts[type].filter(p => p !== prompt)];
        while (recentPrompts[type].length > PROMPTS_RECENT_MAX) {
            recentPrompts[type].shift();
        }
        this.update({ recentPrompts: recentPrompts }, { replaceKeys: true });
        return this.updatePromise;
    },

    async isEduUser() {
        return await Api.eduPlan.get({
            action: "isEduUser"
        });
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        //base64 encode images
        const photoURL = this.getAuthUser().photoURL;
        if (photoURL) {
            try {
                await fetch(photoURL);
            } catch {
                // noop
            }
        }
        return attrs;
    },

    getPresentationIds() {
        return Object.keys(this.get("presentations") ?? {}).filter(key => !["id", "_changeId", "modifiedAt", "createdAt"].includes(key));
    }
});

export {
    User
};

